import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Workflow, CheckCircle2, XCircle, Clock, Zap,
  TrendingUp, Plug2, ArrowRight, Activity,
  Users, Server, Shield, HardDrive,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { clsx } from 'clsx';

const MOCK_EXECUTION_DATA = [
  { date: 'Mon', success: 24, failed: 2 },
  { date: 'Tue', success: 31, failed: 1 },
  { date: 'Wed', success: 18, failed: 4 },
  { date: 'Thu', success: 45, failed: 0 },
  { date: 'Fri', success: 38, failed: 3 },
  { date: 'Sat', success: 12, failed: 1 },
  { date: 'Sun', success: 8, failed: 0 },
];

const MOCK_RECENT_EXECUTIONS = [
  { id: 'ex-001', workflow: 'Employee Onboarding – John Smith', status: 'success', duration: '2m 14s', trigger: 'manual', time: '5 min ago' },
  { id: 'ex-002', workflow: 'VM Provisioning – dev-app-003', status: 'success', duration: '7m 42s', trigger: 'api', time: '23 min ago' },
  { id: 'ex-003', workflow: 'SOAR – Brute Force Response', status: 'running', duration: '1m 08s', trigger: 'webhook', time: '1 min ago' },
  { id: 'ex-004', workflow: 'Snapshot Cleanup – Nightly', status: 'success', duration: '4m 31s', trigger: 'schedule', time: '2h ago' },
  { id: 'ex-005', workflow: 'Inactive User Cleanup', status: 'failed', duration: '0m 42s', trigger: 'schedule', time: '3h ago' },
];

const STATS = [
  { label: 'Total Workflows', value: '47', icon: Workflow, trend: '+3 this week', trendUp: true, color: 'bg-orbayx-500/10 text-orbayx-600' },
  { label: 'Executions Today', value: '124', icon: Zap, trend: '+18%', trendUp: true, color: 'bg-emerald-500/10 text-emerald-400' },
  { label: 'Success Rate', value: '97.2%', icon: CheckCircle2, trend: '+0.4%', trendUp: true, color: 'bg-blue-500/10 text-blue-400' },
  { label: 'Integrations', value: '12', icon: Plug2, trend: '11 active', trendUp: true, color: 'bg-purple-500/10 text-purple-400' },
];

const DOMAIN_CARDS = [
  { label: 'Active Directory', icon: Users, workflows: 7, lastRun: '5 min ago', status: 'healthy', href: '/domains/ad', color: 'border-blue-500/20 hover:border-blue-500/40' },
  { label: 'Security & Cyber', icon: Shield, workflows: 4, lastRun: '1 min ago', status: 'running', href: '/domains/security', color: 'border-red-500/20 hover:border-red-500/40' },
  { label: 'Virtualization', icon: Server, workflows: 6, lastRun: '2h ago', status: 'healthy', href: '/domains/vm', color: 'border-purple-500/20 hover:border-purple-500/40' },
  { label: 'Storage', icon: HardDrive, workflows: 3, lastRun: '6h ago', status: 'warning', href: '/domains/storage', color: 'border-orange-500/20 hover:border-orange-500/40' },
];

const STATUS_COLORS: Record<string, string> = {
  success: 'text-emerald-400',
  failed: 'text-red-400',
  running: 'text-blue-400 animate-pulse',
  queued: 'text-yellow-400',
};

const STATUS_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  success: CheckCircle2,
  failed: XCircle,
  running: Activity,
  queued: Clock,
};

export function Dashboard() {
  const navigate = useNavigate();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Enterprise Automation Overview</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/blueprints')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-600 hover:text-gray-900 transition-all"
          >
            <Zap className="w-4 h-4" />
            Browse Blueprints
          </button>
          <button
            onClick={() => navigate('/builder')}
            className="flex items-center gap-2 px-4 py-2 bg-orbayx-600 hover:bg-orbayx-500 rounded-lg text-sm text-white font-medium transition-all shadow-glow"
          >
            <Workflow className="w-4 h-4" />
            New Workflow
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STATS.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-gray-500">{stat.label}</span>
                <div className={clsx('p-2 rounded-lg', stat.color)}>
                  <Icon className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className={clsx('text-xs mt-1 flex items-center gap-1', stat.trendUp ? 'text-emerald-400' : 'text-red-400')}>
                <TrendingUp className="w-3 h-3" />
                {stat.trend}
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Execution Chart */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-white">Execution History (7 days)</h2>
            <span className="text-xs text-gray-400">Hover for details</span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={MOCK_EXECUTION_DATA}>
              <defs>
                <linearGradient id="successGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="failedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
              />
              <Area type="monotone" dataKey="success" stroke="#6366f1" strokeWidth={2} fill="url(#successGrad)" name="Success" />
              <Area type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} fill="url(#failedGrad)" name="Failed" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Domain Status */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h2 className="text-sm font-semibold text-white mb-4">Automation Domains</h2>
          <div className="space-y-3">
            {DOMAIN_CARDS.map((domain) => {
              const Icon = domain.icon;
              const statusColors: Record<string, string> = {
                healthy: 'text-emerald-400',
                running: 'text-blue-400',
                warning: 'text-yellow-400',
                error: 'text-red-400',
              };
              return (
                <button
                  key={domain.label}
                  onClick={() => navigate(domain.href)}
                  className={clsx(
                    'w-full flex items-center gap-3 p-3 bg-gray-50 border rounded-lg text-left hover:bg-gray-50 transition-all',
                    domain.color,
                  )}
                >
                  <Icon className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-700">{domain.label}</div>
                    <div className="text-xs text-gray-400">{domain.workflows} workflows · {domain.lastRun}</div>
                  </div>
                  <div className={clsx('text-xs font-medium', statusColors[domain.status])}>
                    {domain.status}
                  </div>
                </button>
              );
            })}
          </div>
          <button
            onClick={() => navigate('/blueprints')}
            className="mt-3 w-full flex items-center justify-center gap-1 py-2 text-xs text-orbayx-600 hover:text-orbayx-700 transition-colors"
          >
            View Blueprint Catalog <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Recent Executions */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-white">Recent Executions</h2>
          <button
            onClick={() => navigate('/activity')}
            className="flex items-center gap-1 text-xs text-orbayx-600 hover:text-orbayx-700 transition-colors"
          >
            View all <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <div className="divide-y divide-white/5">
          {MOCK_RECENT_EXECUTIONS.map((exec) => {
            const StatusIcon = STATUS_ICONS[exec.status] || Activity;
            return (
              <div key={exec.id} className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-colors cursor-pointer">
                <StatusIcon className={clsx('w-4 h-4 flex-shrink-0', STATUS_COLORS[exec.status])} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-700 truncate">{exec.workflow}</div>
                  <div className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">
                    <span className="capitalize">{exec.trigger}</span>
                    <span>·</span>
                    <span>{exec.duration}</span>
                  </div>
                </div>
                <div className="text-xs text-gray-400">{exec.time}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
